import React, { useCallback, useRef, useMemo } from 'react';
import { styled } from 'linaria/react';
import { useQuery } from 'react-apollo';

import ProductCarousel from '../../ui/ProductCarousel';
import BestSellerQuery from './BestSellerQuery.gql';
import { Inner } from '../../CategoryPage/CategoryHeader';
import Spinner from '../../ui/Spinner';
import { RefinedProductCard } from '../../CategoryPage/RefinedProductCard';
import { theme } from '../../Theme';
import { useTracking } from '../../../utils/tracking/useTracking';
import { useInViewTracking } from '../../../utils/tracking/useInViewTracking';

const ProductGridWrapper = styled('div')`
  width: 100%;
  max-width: 94rem;
  margin: auto;
  padding: 1rem 2rem;
  ${theme.below.xl} {
    padding: 1rem 1rem;
  }

  ${Inner} & {
    padding: 0rem 0rem;
  }

  h2 {
    text-align: center;
    text-transform: uppercase;
    font-size: 1.5rem;
    letter-spacing: 0.35rem;
    max-width: 90%;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 3rem;
  }
`;

const BestSellers = ({ minPrice, maxPrice, limit, category }) => {
  const { trackViewItemList } = useTracking();
  const { loading, error, data } = useQuery(BestSellerQuery, {
    variables: {
      path: category?.value?.primaryRoute?.path,
      limit: limit?.value
    },
    skip: !category?.value?.primaryRoute?.path,
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache'
  });
  // changing the fetch policy to no-cache means that it wont cache the data to store
  // so the previous conflict with the query in the category page is avoided

  // Memoize filtered products to prevent unnecessary re-renders
  const filteredProducts = useMemo(() => {
    let products = data?.route?.object?.products?.result;

    if (!products) return null;

    if (minPrice?.value) {
      products = products.filter(
        product => product.price.exVat > parseInt(minPrice.value)
      );
    }

    if (maxPrice?.value) {
      products = products.filter(
        product => product.price.exVat < parseInt(maxPrice.value)
      );
    }

    return products.slice(0, parseInt(limit.value));
  }, [data?.route?.object?.products?.result, minPrice?.value, maxPrice?.value, limit.value]);

  // Track when best sellers come into view
  const hasTrackedRef = useRef(false);

  const handleInView = useCallback(() => {
    if (hasTrackedRef.current) return;
    if (!filteredProducts || filteredProducts.length === 0) return;

    trackViewItemList({
      products: filteredProducts,
      listName: `Best Sellers - ${data?.route?.object?.name || 'Unknown'}`,
      listId: 'bestsellers'
    });

    hasTrackedRef.current = true;
  }, [filteredProducts, data?.route?.object?.name, trackViewItemList]);

  // Reset tracking flag when products change
  React.useEffect(() => {
    hasTrackedRef.current = false;
  }, [filteredProducts]);

  const trackingRef = useInViewTracking(handleInView, { threshold: 0.3, triggerOnce: true });

  if (loading) return <Spinner />;
  if (error) return null;
  if (!data) return <div>No data available.</div>;

  return (
    <ProductGridWrapper ref={trackingRef}>
      <ProductCarousel
        products={filteredProducts}
        listName={`Best Sellers - ${data.route.object.name}`}
        categoryPath={data.route.object.primaryRoute}
        loading={loading}
        ProductComponent={props => <RefinedProductCard {...props} as="div" />}
        toShow={filteredProducts.length < 5 ? filteredProducts.length : 5}
      />
    </ProductGridWrapper>
  );
};

export default BestSellers;
